import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { drw, errText, verifyThumbUrl } from "./api";
import { useWheelZoom } from "./PagePreview";

const frac = (e, el) => {
  const r = el.getBoundingClientRect();
  return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
};
const norm = (a, b) => [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];

/** DRW-5 §1 — draw the sheet-NUMBER box and the TITLE box once on a sample page; the server
 *  re-reads those two regions on every page (or the ticked subset) and the results come back for
 *  review. Nothing lands in the grid until "Apply", and every field stays editable after. */
export default function RegionReread({ uploadId, pages, selected, onApply, onClose }) {
  const [sample, setSample] = useState(0);
  const [which, setWhich] = useState("number");          // number | title
  const [mode, setMode] = useState("draw");              // draw | pan
  const [boxes, setBoxes] = useState({ number: null, title: null });
  const [drag, setDrag] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  // SHEET-NUM-1 — a title block is a few per cent of a sheet, so the sample needs zoom and pan
  // before a box can be drawn on it accurately. Fractions are read off the image's own rectangle,
  // which already includes the zoom, so a box drawn at 300% lands in the right place.
  const [z, setZ] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [sampleRead, setSampleRead] = useState(null);
  const [panning, setPanning] = useState(null);
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const zoomStep = useCallback((d) => setZ((v) => Math.min(6, Math.max(1, Number((v + d).toFixed(2))))), []);
  useWheelZoom(stageRef, zoomStep);
  const p = pages[sample];
  const subset = selected.size ? pages.filter((pg) => selected.has(`${pg.part_index}-${pg.page_index}`)) : null;

  const down = (e) => {
    e.preventDefault();
    if (mode === "pan" || e.button === 1) { setPanning({ x: e.clientX - pan.x, y: e.clientY - pan.y }); return; }
    imgRef.current.setPointerCapture?.(e.pointerId);
    setDrag({ a: frac(e, imgRef.current), b: frac(e, imgRef.current) });
  };
  const move = (e) => {
    if (panning) { setPan({ x: e.clientX - panning.x, y: e.clientY - panning.y }); return; }
    if (drag) setDrag((d) => ({ ...d, b: frac(e, imgRef.current) }));
  };
  const up = () => {
    if (panning) { setPanning(null); return; }
    if (!drag) return;
    const r = norm(drag.a, drag.b);
    setDrag(null);
    if (r[2] - r[0] < 0.005 || r[3] - r[1] < 0.005) return;
    setBoxes((b) => ({ ...b, [which]: r }));
    if (which === "number" && !boxes.title) setWhich("title");
    setResults(null);
    setSampleRead(null);
  };

  const reread = async () => {
    if (!boxes.number) { toast.error("Draw the sheet-number box first"); return; }
    setBusy(true);
    try {
      const { data } = await drw.post(`/uploads/${uploadId}/regions`, {
        number_region: boxes.number, title_region: boxes.title,
        pages: subset ? subset.map((pg) => ({ part_index: pg.part_index, page_index: pg.page_index })) : null }, { _noQueue: true, timeout: 120000 });
      setResults(data);
    } catch (e) { toast.error(errText(e, "Could not re-read those regions")); } finally { setBusy(false); }
  };

  /** SHEET-NUM-1 — read the two boxes on the sample page only, so the box can be checked before
   *  it is fired at 62 pages. Same endpoint, one page. */
  const tryHere = async () => {
    if (!boxes.number) { toast.error("Draw the sheet-number box first"); return; }
    setBusy(true);
    try {
      const { data } = await drw.post(`/uploads/${uploadId}/regions`, {
        number_region: boxes.number, title_region: boxes.title,
        pages: [{ part_index: p.part_index, page_index: p.page_index }] }, { _noQueue: true, timeout: 60000 });
      setSampleRead(data.pages?.[0] || { sheet_number: "", title: "" });
    } catch (e) { toast.error(errText(e, "Could not read that box")); } finally { setBusy(false); }
  };

  const changed = (results?.pages || []).filter((r) => {
    const cur = pages.find((pg) => pg.part_index === r.part_index && pg.page_index === r.page_index);
    return cur && ((r.sheet_number && r.sheet_number !== cur.sheet_number) || (r.title && r.title !== cur.title));
  });
  const rectStyle = (r, color) => r && ({ position: "absolute", left: `${r[0] * 100}%`, top: `${r[1] * 100}%`, width: `${(r[2] - r[0]) * 100}%`, height: `${(r[3] - r[1]) * 100}%`, border: `2px solid ${color}`, background: `${color}22`, pointerEvents: "none" });

  return (
    <div className="border-b bg-sky-50/60 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_18rem] gap-3 text-xs" data-testid="region-tool">
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sky-900">Read by region</span>
          <label>Sample page
            <select value={sample} onChange={(e) => { setSample(Number(e.target.value)); }} className="ml-1 h-7 border rounded px-1 bg-white" data-testid="region-sample">
              {pages.map((pg, i) => <option key={`${pg.part_index}-${pg.page_index}`} value={i}>page {pg.page_index + 1}{pg.sheet_number ? ` · ${pg.sheet_number}` : ""}</option>)}
            </select>
          </label>
          <div className="flex rounded-md border overflow-hidden">
            {["number", "title"].map((w) => (
              <button key={w} type="button" onClick={() => setWhich(w)} data-testid={`region-which-${w}`}
                      className={`px-2 h-7 ${which === w ? "bg-sky-700 text-white" : "bg-white"}`}>{w === "number" ? "1 · Number box" : "2 · Title box"}{boxes[w] ? " ✓" : ""}</button>
            ))}
          </div>
          <span className="text-neutral-500">{which === "number" ? "Drag a box around the SHEET NUMBER" : "Drag a box around the SHEET TITLE"}</span>
        </div>
        <div className="flex items-center gap-1 text-[11px]">
          <div className="flex rounded-md border overflow-hidden">
            {["draw", "pan"].map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} data-testid={`region-mode-${m}`}
                      className={`px-2 h-7 ${mode === m ? "bg-neutral-800 text-white" : "bg-white"}`}>{m === "draw" ? "Draw box" : "Pan"}</button>
            ))}
          </div>
          <button type="button" onClick={() => zoomStep(-0.5)} className="w-7 h-7 rounded border bg-white" data-testid="region-zoom-out">−</button>
          <span className="tabular-nums w-10 text-center" data-testid="region-zoom-level">{Math.round(z * 100)}%</span>
          <button type="button" onClick={() => zoomStep(0.5)} className="w-7 h-7 rounded border bg-white" data-testid="region-zoom-in">+</button>
          <button type="button" onClick={() => { setZ(1); setPan({ x: 0, y: 0 }); }} className="px-2 h-7 rounded border bg-white" data-testid="region-zoom-fit">Fit</button>
          {(boxes.number || boxes.title) && (
            <button type="button" onClick={() => { setBoxes({ number: null, title: null }); setResults(null); setSampleRead(null); setWhich("number"); }}
                    className="px-2 h-7 rounded border bg-white underline" data-testid="region-clear-boxes">Clear boxes</button>
          )}
          <span className="text-neutral-500">Zoom in on the title block, then draw. Drawing again replaces that box.</span>
        </div>
        <div ref={stageRef} className="relative max-h-[62vh] overflow-hidden border rounded bg-neutral-50 grid place-items-center">
        <div className="relative inline-block select-none touch-none"
             style={{ cursor: mode === "pan" ? "grab" : "crosshair", transform: `translate(${pan.x}px, ${pan.y}px) scale(${z})`, transformOrigin: "center" }}>
          <img ref={imgRef} src={verifyThumbUrl(uploadId, p.part_index, p.page_index, 1800)} alt={`page ${p.page_index + 1}`} draggable={false}
               className="max-h-[60vh] max-w-full bg-white" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { setDrag(null); setPanning(null); }}
               data-testid="region-sample-image" />
          {boxes.number && <div style={rectStyle(boxes.number, "#c2410c")} data-testid="region-box-number" />}
          {boxes.title && <div style={rectStyle(boxes.title, "#1d4ed8")} data-testid="region-box-title" />}
          {drag && <div style={rectStyle(norm(drag.a, drag.b), which === "number" ? "#c2410c" : "#1d4ed8")} />}
        </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-neutral-700">{subset ? <>Applies to the <b>{subset.length} ticked</b> page(s) — a second title-block layout.</> : <>Applies to <b>all {pages.length}</b> pages. Tick pages in the grid to limit it.</>}</div>
        <button type="button" disabled={busy || !boxes.number} onClick={tryHere}
                className="w-full min-h-9 rounded-md border bg-white font-semibold disabled:opacity-50" data-testid="region-try-here">
          {busy ? "Reading…" : "What does this box read here?"}
        </button>
        {sampleRead && (
          <div className="rounded border bg-white p-2 space-y-0.5" data-testid="region-sample-read">
            <div>Number: <b className="font-mono">{sampleRead.sheet_number || "— nothing in that box"}</b></div>
            {boxes.title && <div>Title: <b>{sampleRead.title || "— nothing in that box"}</b></div>}
            <div className="text-neutral-500">If that is wrong, move the box and read again.</div>
          </div>
        )}
        <button type="button" disabled={busy || !boxes.number} onClick={reread} className="w-full min-h-11 rounded-md bg-sky-700 text-white font-semibold disabled:opacity-50" data-testid="region-reread">
          {busy ? "Reading…" : `Re-read ${subset ? subset.length : pages.length} page(s)`}
        </button>
        {results && (
          <div className="space-y-1" data-testid="region-results">
            <div><b>{results.found}</b> of {results.total} pages gave a sheet number · <b data-testid="region-changed-count">{changed.length}</b> would change</div>
            <div className="max-h-40 overflow-auto border rounded bg-white divide-y">
              {results.pages.map((r) => {
                const cur = pages.find((pg) => pg.part_index === r.part_index && pg.page_index === r.page_index) || {};
                const diff = r.sheet_number && r.sheet_number !== cur.sheet_number;
                return (
                  <div key={`${r.part_index}-${r.page_index}`} className={`px-2 py-1 flex gap-2 ${!r.found_number ? "text-red-700" : diff ? "text-emerald-800" : "text-neutral-500"}`} data-testid={`region-result-${r.page_index}`}>
                    <span className="w-12 shrink-0">p{r.page_index + 1}</span>
                    <span className="font-mono">{cur.sheet_number || "—"}{diff ? ` → ${r.sheet_number}` : r.found_number ? "" : " → (nothing read)"}</span>
                    {r.title && r.title !== cur.title && <span className="truncate text-neutral-500">· {r.title}</span>}
                  </div>
                );
              })}
            </div>
            <button type="button" disabled={!results.found} onClick={() => { onApply(results.pages.filter((r) => r.found_number)); toast.success(`Applied to ${results.found} page(s) — still editable below`); }}
                    className="w-full min-h-11 rounded-md bg-orange-500 text-white font-semibold disabled:opacity-50" data-testid="region-apply">Apply to the grid</button>
          </div>
        )}
        <button type="button" onClick={onClose} className="w-full min-h-11 rounded-md border bg-white" data-testid="region-close">Close</button>
      </div>
    </div>
  );
}
